/* FacilityFormV2.jsx — fully i18n-enabled */
import 'html2canvas';
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Button, Container, Typography, Grid, Paper, TextField, MenuItem,
  Alert, Box, FormControlLabel, Checkbox, Tooltip, InputAdornment,
  Divider, Snackbar,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { initKeycloak } from '../keycloak';
import { useTranslation } from 'react-i18next';
import { getStoredReferralCode } from '../utils/referral';
import { getAttribution } from '../utils/attribution';

/* -------- Config -------- */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const EVALUATION_END = 30;

const FACILITY_TYPES = [
  ['restaurant', 'restaurant'],
  ['gite', 'gite'],
  ['bar', 'bar'],
  ['hotel', 'hotel'],
  ['museum', 'museum'],
  ['shop', 'shop'],
  ['pharmacy', 'pharmacy'],
  ['bakery', 'bakery'],
  ['activity', 'activity'],
  ['camping', 'camping'],
  ['fitness_center', 'gym'],
  ['other', 'other'],
];

export default function FacilityFormV2() {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const emailFromUrl = searchParams.get('email') || '';

  const [token, setToken] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const kc = await initKeycloak();
        if (kc.authenticated) setToken(kc.token ?? '');
      } catch (e) {
        console.error('Keycloak error', e);
      }
    })();
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    lon: '0.0',
    lat: '0.0',
    facility_type: 'other',
    description: '',
    L: '',
    C: '',
    emailAddress: emailFromUrl,
    current_registration_step: 'unstarted',
    owner_email: emailFromUrl,
    evaluationDays: EVALUATION_END,
    business_id: '',
    business_proof_url: '',
    contact_phone: '',
    business_reservation_url: '',
    business_url: '',
  });

  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successSnack, setSuccessSnack] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting || !declarationChecked) return;
    if (!token) {
      setError(t('facilityForm.errors.noToken'));
      return;
    }

    const payload = {
      ...formData,
      lon: Number(formData.lon) || 0,
      lat: Number(formData.lat) || 0,
      facility_type: formData.facility_type,
      // Whatever ?ref= code was captured on first landing, however long
      // ago - null if this visitor never came through a referral link.
      referral_code: getStoredReferralCode(),
      attribution: getAttribution(),
    };

    setIsSubmitting(true);

    try {
      const r = await fetch(`${API_BASE_URL}/v2/facilities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!r.ok) {
        // Free month limits (backend/utils/freeTrial.ts): say why, not just "failed".
        const body = await r.json().catch(() => ({}));
        if (body.code === 'FREE_TRIAL_LIMIT' || body.code === 'FREE_TRIAL_OVER') {
          setError(t(`facilityForm.errors.${body.code === 'FREE_TRIAL_LIMIT' ? 'freeTrialLimit' : 'freeTrialOver'}`));
          return;
        }
        throw new Error(`HTTP ${r.status}`);
      }

      setSuccessSnack(true);
      setTimeout(() => navigate('/showDashboard'), 2500);
    } catch (err) {
      console.error(err);
      setError(t('facilityForm.errors.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t('facilityForm.title')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {/* ---- Identity ---- */}
          <Divider sx={{ my: 3 }}>{t('facilityForm.sections.identity')}</Divider>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label={t('facilityForm.name')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                helperText={t('facilityForm.nameHelp')}
                placeholder={t('facilityForm.placeholder.name')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label={t('facilityForm.address')}
                name="address"
                value={formData.address}
                onChange={handleChange}
                helperText={t('facilityForm.addressHelp')}
                placeholder={t('facilityForm.placeholder.address')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                name="facility_type"
                label={t('facilityForm.businessType')}
                value={formData.facility_type}
                onChange={handleChange}
                helperText={t('facilityForm.businessTypeHelp')}
              >
                {FACILITY_TYPES.map(([value, key]) => (
                  <MenuItem key={value} value={value}>
                    {t(`facilityForm.types.${key}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label={t('facilityForm.description')}
                name="description"
                value={formData.description}
                onChange={handleChange}
                helperText={t('facilityForm.descriptionHelp')}
                placeholder={t('facilityForm.placeholder.description')}
              />
            </Grid>
          </Grid>

          {/* ---- Location & Contact ---- */}
          <Divider sx={{ my: 3 }}>{t('facilityForm.sections.location')}</Divider>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                disabled
                label={t('facilityForm.longitude')}
                name="lon"
                value={formData.lon}
                helperText={t('facilityForm.geoAuto')}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                disabled
                label={t('facilityForm.latitude')}
                name="lat"
                value={formData.lat}
                helperText={t('facilityForm.geoAuto')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label={t('facilityForm.city')}
                name="L"
                value={formData.L}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label={t('facilityForm.countryCode')}
                name="C"
                inputProps={{ maxLength: 2 }}
                value={formData.C}
                onChange={handleChange}
                helperText={t('facilityForm.countryCodeHelp')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                type="email"
                label={t('facilityForm.facilityEmail')}
                name="emailAddress"
                value={formData.emailAddress}
                onChange={handleChange}
                helperText={t('facilityForm.facilityEmailHelp')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label={t('facilityForm.contactPhone')}
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* ---- Legal ---- */}
          <Divider sx={{ my: 3 }}>{t('facilityForm.sections.legal')}</Divider>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label={t('facilityForm.businessId')}
                name="business_id"
                value={formData.business_id}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label={t('facilityForm.proofOfOwnership')}
                name="business_proof_url"
                value={formData.business_proof_url}
                onChange={handleChange}
                helperText={t('facilityForm.proofOfOwnershipHelp')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t('facilityForm.tooltip.proofPrivacy')}>
                        <InfoOutlinedIcon sx={{ cursor: 'pointer', color: 'text.secondary' }} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* ---- Optional Links ---- */}
          <Divider sx={{ my: 3 }}>{t('facilityForm.sections.optional')}</Divider>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('facilityForm.reservationUrl')}
                name="business_reservation_url"
                value={formData.business_reservation_url}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('facilityForm.website')}
                name="business_url"
                value={formData.business_url}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* ---- Declaration ---- */}
          <Divider sx={{ my: 3 }}>{t('facilityForm.sections.declaration')}</Divider>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={declarationChecked}
                    onChange={(e) => setDeclarationChecked(e.target.checked)}
                  />
                }
                label={t('facilityForm.declaration')}
              />
            </Grid>
          </Grid>

          <input type="hidden" name="evaluationDays" value={formData.evaluationDays} />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            disabled={isSubmitting || !declarationChecked}
          >
            {isSubmitting ? t('facilityForm.sending') : t('facilityForm.submit')}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={successSnack}
        autoHideDuration={3000}
        onClose={() => setSuccessSnack(false)}
        message={t('facilityForm.success')}
      />
    </Container>
  );
}
